import { useState, useEffect, createContext, useContext } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Wrapper to also store in localStorage
  const setUser = (userData, token = null) => {
    setUserState(userData);
    if (userData) {
      localStorage.setItem('wyco_user', JSON.stringify(userData));
      if (token) {
        localStorage.setItem('wyco_session_token', token);
        setSessionToken(token);
      }
    } else {
      localStorage.removeItem('wyco_user');
      localStorage.removeItem('wyco_session_token');
      setSessionToken(null);
    }
  };

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = sessionToken || localStorage.getItem('wyco_session_token');
    if (token) {
      return { 'Authorization': `Bearer ${token}` };
    }
    return {};
  };

  useEffect(() => {
    // Check localStorage first for faster initial load
    const stored = localStorage.getItem('wyco_user');
    const storedToken = localStorage.getItem('wyco_session_token');
    if (stored) {
      try {
        setUserState(JSON.parse(stored));
        if (storedToken) {
          setSessionToken(storedToken);
        }
      } catch (e) {
        localStorage.removeItem('wyco_user');
        localStorage.removeItem('wyco_session_token');
      }
    }
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('wyco_session_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include',
        headers
      });
      if (response.ok) {
        const userData = await response.json();
        setUserState(userData);
        localStorage.setItem('wyco_user', JSON.stringify(userData));
      } else if (response.status === 401) {
        // Only clear session on explicit 401 Unauthorized
        // Don't clear on 500/502/503 server errors
        setUser(null);
      } else {
        // Server error - keep current session, just log the error
        console.warn('Auth check returned non-ok status:', response.status);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Keep localStorage user if network error (offline support)
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const login = () => {
    const redirectUrl = window.location.origin + '/sentinel';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  const processSessionId = async (sessionId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });
      
      if (response.ok) {
        const userData = await response.json();
        // Store the session token from response for backup auth
        const token = userData.session_token;
        setUser(userData, token);
        return userData;
      }
      throw new Error('Session exchange failed');
    } catch (error) {
      console.error('Session processing failed:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, processSessionId, checkAuth, getAuthHeaders, sessionToken }}>
      {children}
    </AuthContext.Provider>
  );
};
